import { useEffect, useState, useRef } from 'react'
import { MarkerData } from 'Types/markers'
import styled, { useTheme } from 'styled-components'
import { device } from '../breakpoints'
import { Button } from '@mui/material'
import locales from 'locales/locales'
import { FaHelmetSafety } from "react-icons/fa6"
import { FaPeopleGroup } from "react-icons/fa6";
import { PiCalendarCheckFill } from "react-icons/pi";

declare global {
  interface Window {
    dataLayer: any
  }
}

function getDistanceFromLatLonInKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) {
  var R = 6371 // Radius of the earth in km
  var dLat = deg2rad(lat2 - lat1) // deg2rad below
  var dLon = deg2rad(lon2 - lon1)
  var a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  var d = R * c // Distance in km
  return d
}

function deg2rad(deg: number) {
  return (deg * Math.PI) / 180
}

function getDistanceStr(meters: number) {
  if (meters < 1000) {
    return (Math.round(meters / 10) * 10).toString() + 'm'
  } else if (meters < 4000) {
    return (Math.round(meters / 100) / 10).toString() + 'km'
  } else {
    return Math.round(meters / 1000).toString() + 'km'
  }
}

export const RestosList = ({
  items,
  publisher,
  handleClick,
  userLocation,
}: {
  items: MarkerData[]
  publisher: string
  handleClick: (marker: MarkerData) => void
  userLocation: { lat: number; lng: number } | null | undefined
}) => {
  const countStep = 12
  const [showCount, setShowCount] = useState(countStep)
  const [sortedItems, setSortedItems] = useState<MarkerData[]>([])
  const loader = useRef(null)
  const theme = useTheme()

  // If there is a new list of items or a new user location, add the distance to the user and sort them
  useEffect(() => {
    if (userLocation) {
      const list = items.map((item) => ({
        ...item,
        distance: getDistanceFromLatLonInKm(
          userLocation.lat,
          userLocation.lng,
          item.latitude,
          item.longitude
        ),
      }))

      setSortedItems(list.sort((a, b) => a.distance - b.distance))
    } else {
      setSortedItems(items)
    }
  }, [items, userLocation])

  return (
    <Container>
      <ListContent>
        {sortedItems.slice(0, showCount).map((item) => (
          <ItemContainer
            publisher={publisher}
            // className={item.main_style}
            key={`item-${item.markerId}`}
            onClick={() => {
              handleClick(item)

              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'Interactions',
                event_action: 'click',
                event_label: `showitem:${item.markerId}`,
              })
            }}
          >
            <ImageContainer>
              {item.image ? (
                <BackgroundImage src={item.image} />
              ) : (
                <Placeholder />
              )}
            </ImageContainer>
            <ItemContent>
              <div>
                <ItemTitle>{item.name}</ItemTitle>
                <ItemLocation>{item.municipality}</ItemLocation>

                <ListInfos>
                <ItemInfo><PiCalendarCheckFill /> {item.date_str}</ItemInfo>
                <div>
                {item.pop > 0 && <ItemInfo><FaPeopleGroup /> <strong>{item.pop}</strong> habitants</ItemInfo>}
                {item.jobs > 0 && <ItemInfo><FaHelmetSafety /> <strong>{item.jobs}</strong> emplois</ItemInfo>}
                </div>
                {item?.distance && (
                  <ItemDistance>
                    {getDistanceStr(item?.distance * 1000)}
                  </ItemDistance>
                )}
                </ListInfos>
              </div>
            </ItemContent>
          </ItemContainer>
        ))}
      </ListContent>
      <div
        ref={loader}
        style={{ padding: '20px', display: 'flex', justifyContent: 'center' }}
      >
        {sortedItems.length > showCount && (
          <Button
            variant='contained'
            disableElevation
            onClick={() => {
              setShowCount(showCount + countStep)

              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'Interactions',
                event_action: 'click',
                event_label: `showmore`,
              })
            }}
          >
            {locales.buttonSeeMore[theme.lang]}
          </Button>
        )}
      </div>
    </Container>
  )
}

const ItemContent = styled.div`
  width: 100%;
  background: ${(props) => props.theme.plainColor};
  color: ${(props) => props.theme.textColor};
  overflow: hidden;
  // display: grid;
  // grid-template-columns: 2fr 1fr;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    padding-top: 0.65em;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    padding-top: 0.65em;
  }
  margin-bottom: 1rem;
`

const ListInfos = styled.div`
  margin-top: 0.45em;
  padding-top: 0.45em;
  font-size: 0.7825em;
  opacity: 0.65;
  line-height: 1.2em;
  color: var(--meta-data);
`

const ItemTitle = styled.div`
  font-family: var(--font-sans-serif);
  font-weight: 600;
  font-size: 1.1em;
  line-height: 1.2em;
`

const ItemDistance = styled.div`
  font-size: 0.8em;
  color: #a2a3a5;
`
const ItemDates = styled.div`
  font-family: var(--font-sans-serif);
  overflow: hidden;
  position: relative;
  font-size: 0.865em;
  line-height: 1.2em;
  opacity: 0.45;
`

const ItemLocation = styled.div`
font-family: var(--font-sans-serif);
opacity: 0.45;
font-size: 0.96em;
`

const ItemInfo = styled(ItemDates)`
  opacity: 1;
  display: flex;
  align-items: center;

  strong {
    margin-right: 0.25em;
  }

  svg {
    margin-right: 0.5em;
  }
`

const ItemDescription = styled.div`
  font-family: var(--font-sans-serif);
  overflow: hidden;
  width: calc(50%);
  position: relative;
  font-size: 0.865em;
  line-height: 1.2em;
  text-overflow: ellipsis;
  opacity: 0.55;
  display: block;
  white-space: nowrap;
  margin-top: 1rem;
  margin-bottom: 1rem;
`
const ItemStyles = styled.div`
  margin-top: 1rem;
  margin-bottom: 1rem;
`
const Container = styled.div`
  position: relative;
`

const ListContent = styled.div`
  position: relative;
  display: grid;
  padding: 1em;
  padding-top: 0;
  grid-auto-rows: minmax(min-content, max-content);

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    grid-template-columns: 1fr;
    grid-gap: 30px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 20px;
  }

  @media ${device.laptopL} {
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 20px;
  }
`

const ItemContainer = styled.div<{ publisher?: string }>`
  display: grid;
  position: relative;
  cursor: pointer;
  width: 100%;
  overflow: hidden;

  &:last-child {
    border: none;
  }

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(min-content, max-content) 1fr;
  }
`

const InlineFilter = styled.div`
  display: inline-block;
  padding: 0.25em 1rem;
  background: ${(props) => props.theme.greyColor};
  color: ${(props) => props.theme.textColor};
  border-radius: 5px;
  margin-right: 1rem;
  margin-bottom: 1rem;
  font-size: 1.5rem;

  &:last-child {
    margin-right: 0;
  }
`

const ImageContainer = styled.div`
  height: 100%;
  width: 100%;
  background-color: ${(props) => props.theme.mainColor};
`

const Image = `
height: 100%;
min-height: 150px;
background-size: cover;
background-position: center center;
`

const BackgroundImage = styled.div<{ src: string }>`
  ${Image}
  background-image: url(https://interaktiv-mf.tagesanzeiger.ch/2023/ouest-lausannois/img/${(
    props
  ) => props.src});
`

const Placeholder = styled.div`
  ${Image}
  background-image: url(https://interaktiv-mf.tagesanzeiger.ch/2023/ouest-lausannois/img/placeholder-rect.jpg);
  background-blend-mode: multiply;
  background-color: #dcdee1;
`
